import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Users, BookOpen, MessageCircle, Settings, BarChart3, AlertCircle, Trash2, ArrowUpRight } from 'lucide-react';
import { toast } from 'react-toastify';
import { API, axios } from './adminConfig';

const StatCard = ({ title, value, icon, gradient }) => {
  return (
    <Card className={`bg-gradient-to-br ${gradient} border-0 shadow-lg`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-burgundy/70">{title}</p>
            <p className="text-3xl font-bold text-burgundy">{value}</p>
          </div>
          {icon}
        </div>
      </CardContent>
    </Card>
  );
};

const ConversionAlerts = () => {
  const [alerts, setAlerts] = useState(null);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const res = await axios.get(`${API}/admin/conversion-alerts`);
        setAlerts(res.data);
      } catch (err) {
        console.error('Error fetching conversion alerts:', err);
      }
    };
    fetchAlerts();
  }, []);

  const markSeen = async () => {
    try {
      await axios.post(`${API}/admin/conversion-alerts/mark-seen`);
      setAlerts(prev => prev ? { ...prev, unseen_count: 0, events: prev.events.map(e => ({ ...e, seen: true })) } : prev);
    } catch (err) {
      console.error('Error marking alerts seen:', err);
    }
  };

  if (!alerts || alerts.events.length === 0) return null;

  const pillarLabels = { BODY: 'Radiant Body', MIND: 'Balanced Mind', VITALITY: 'Enduring Vitality' };
  const pillarStyles = {
    BODY: 'bg-dark-teal text-cream',
    MIND: 'bg-[#002447] text-cream',
    VITALITY: 'bg-[#D4AF37] text-burgundy'
  };

  return (
    <Card className="border-emerald-200 bg-emerald-50/50" data-testid="conversion-alerts">
      <CardHeader className="pb-3">
        <CardTitle className="text-emerald-800 font-serif flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <ArrowUpRight className="h-5 w-5" />
            Quiz Lead Conversions
            {alerts.unseen_count > 0 && (
              <Badge className="bg-emerald-600 text-white text-xs">{alerts.unseen_count} new</Badge>
            )}
          </div>
          {alerts.unseen_count > 0 && (
            <Button variant="ghost" size="sm" onClick={markSeen} className="text-emerald-600 hover:bg-emerald-100 text-xs">
              Mark all seen
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {alerts.events.slice(0, 5).map((event, i) => (
            <div
              key={event.id || i}
              className={`flex items-center justify-between p-2.5 rounded-lg text-sm ${
                event.seen ? 'bg-white/50' : 'bg-white border border-emerald-200'
              }`}
              data-testid={`conversion-alert-${i}`}
            >
              <div className="flex items-center gap-2">
                {!event.seen && <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />}
                <span className="font-medium text-burgundy">{event.name || event.email}</span>
                <span className="text-burgundy/50">converted to member</span>
                {event.primary_pillar && (
                  <Badge className={`${pillarStyles[event.primary_pillar] || 'bg-burgundy/10 text-burgundy'} text-xs`}>
                    {pillarLabels[event.primary_pillar] || event.primary_pillar}
                  </Badge>
                )}
              </div>
              <span className="text-xs text-burgundy/40 whitespace-nowrap">
                {event.converted_at ? new Date(event.converted_at).toLocaleDateString() : ''}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const DashboardTab = ({ stats, onRefresh }) => {
  const [clearing, setClearing] = useState(false);

  const handleClearTestData = async () => {
    if (!confirm('⚠️ This will DELETE all test users (Registration Flow Test) and test data. Your admin account will be preserved. Continue?')) {
      return;
    }

    setClearing(true);
    try {
      const response = await axios.post(`${API}/admin/clear-test-data`);
      toast.success(response.data.message || 'Test data cleared successfully');
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error clearing test data:', error);
      toast.error('Failed to clear test data');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-burgundy font-serif">Dashboard Overview</h2>
        <Button
          onClick={onRefresh}
          variant="outline"
          size="sm"
          className="border-burgundy text-burgundy hover:bg-burgundy/10"
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          Refresh Stats
        </Button>
      </div>

      {/* Conversion Alerts */}
      <ConversionAlerts />
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={stats.total_users || 0}
          icon={<Users className="h-8 w-8 text-burgundy" />}
          gradient="from-burgundy/10 to-burgundy/5"
        />
        <StatCard
          title="Content Items"
          value={stats.total_content || 0}
          icon={<BookOpen className="h-8 w-8 text-dark-teal" />}
          gradient="from-dark-teal/10 to-dark-teal/5"
        />
        <StatCard
          title="Community Topics"
          value={stats.community?.topics || 0}
          icon={<MessageCircle className="h-8 w-8 text-gold" />}
          gradient="from-gold/10 to-gold/5"
        />
        <StatCard
          title="Chat Messages"
          value={stats.total_messages || 0}
          icon={<Settings className="h-8 w-8 text-burgundy" />}
          gradient="from-burgundy/10 to-burgundy/5"
        />
      </div>

      {/* Subscription Breakdown */}
      {stats.subscription_breakdown && (
        <Card className="border-burgundy/20">
          <CardHeader>
            <CardTitle className="text-burgundy font-serif">Subscription Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">{stats.subscription_breakdown.trial}</p>
                <p className="text-sm text-blue-700 mt-1">Trial Users</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">{stats.subscription_breakdown.active_paid}</p>
                <p className="text-sm text-green-700 mt-1">Active Paid</p>
              </div>
              <div className="text-center p-4 bg-emerald-50 rounded-lg">
                <p className="text-3xl font-bold text-emerald-600">{stats.subscription_breakdown.forever_free}</p>
                <p className="text-sm text-emerald-700 mt-1">Founding 48 / Free</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-600">{stats.subscription_breakdown.cancelled}</p>
                <p className="text-sm text-gray-700 mt-1">Cancelled</p>
              </div>
              {stats.subscription_breakdown.expired > 0 && (
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <p className="text-3xl font-bold text-orange-600">{stats.subscription_breakdown.expired}</p>
                  <p className="text-sm text-orange-700 mt-1">Expired</p>
                </div>
              )}
              {stats.subscription_breakdown.no_status > 0 && (
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <p className="text-3xl font-bold text-amber-600">{stats.subscription_breakdown.no_status}</p>
                  <p className="text-sm text-amber-700 mt-1">No Status</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clear Test Data Section */}
      <Card className="border-red-200 bg-red-50">
        <CardHeader>
          <CardTitle className="text-red-700 flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            Clear Test Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600 mb-4">
            Remove all test users (Registration Flow Test), test coupon usage, and reset analytics. Your admin account will be preserved.
          </p>
          <Button
            onClick={handleClearTestData}
            disabled={clearing}
            variant="destructive"
            className="bg-red-600 hover:bg-red-700"
          >
            {clearing ? (
              <>
                <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full"></div>
                Clearing...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All Test Data
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export { DashboardTab, StatCard };
